import { useState } from 'react';
import CreateCustomerModal from './CreateCustomerModal';

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  loyaltyPoints: number;
  memberSince: string;
  totalSpent: number;
}

interface PhoneLookupModalProps {
  onClose: () => void;
  onSelectCustomer: (customer: any) => void;
}

export default function PhoneLookupModal({ onClose, onSelectCustomer }: PhoneLookupModalProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const customers: Customer[] = [
    {
      id: 'C001',
      name: 'Sarah Johnson',
      email: 'sarah.j@email.com',
      phone: '555-0123',
      loyaltyPoints: 1250,
      memberSince: 'Jan 2023',
      totalSpent: 3450.00
    },
    {
      id: 'C002',
      name: 'Michael Chen',
      email: 'mchen@email.com',
      phone: '555-0124',
      loyaltyPoints: 890,
      memberSince: 'Mar 2023',
      totalSpent: 2180.00
    },
    {
      id: 'C003',
      name: 'Emily Rodriguez',
      email: 'emily.r@email.com',
      phone: '555-0125',
      loyaltyPoints: 2340,
      memberSince: 'Nov 2022',
      totalSpent: 5670.00
    },
    {
      id: 'C004',
      name: 'David Kim',
      email: 'dkim@email.com',
      phone: '555-0126',
      loyaltyPoints: 450,
      memberSince: 'Jun 2023',
      totalSpent: 1230.00
    },
    {
      id: 'C005',
      name: 'Jessica Martinez',
      email: 'jmartinez@email.com',
      phone: '555-0127',
      loyaltyPoints: 1680,
      memberSince: 'Feb 2023',
      totalSpent: 4120.00
    }
  ];

  const handleSearch = () => {
    if (!phoneNumber.trim()) return;

    setIsSearching(true);
    setHasSearched(false);

    setTimeout(() => {
      const results = customers.filter(customer =>
        customer.phone.includes(phoneNumber.replace(/\D/g, ''))
      );

      setSearchResults(results);
      setIsSearching(false);
      setHasSearched(true);
    }, 800);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectCustomer = (customer: Customer) => {
    onSelectCustomer(customer);
    onClose();
  };

  const handleCreateNewCustomer = () => {
    setShowCreateModal(true);
  };

  const handleCustomerCreated = (customer: any) => {
    onSelectCustomer(customer);
    setShowCreateModal(false);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="bg-teal-600 text-white px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold whitespace-nowrap">Phone Lookup</h2>
              <p className="text-sm text-teal-100 whitespace-nowrap">Search customer by phone number</p>
            </div>
            <button 
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center hover:bg-teal-700 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-gray-400 text-xl"></i>
                  </div>
                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter phone number (e.g., 555-0123)"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 text-sm"
                    autoFocus
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={isSearching || !phoneNumber.trim()}
                  className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-colors ${
                    isSearching || !phoneNumber.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer'
                  }`}
                >
                  {isSearching ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Searching...
                    </>
                  ) : (
                    <>
                      <i className="ri-search-line mr-2"></i>
                      Search
                    </>
                  )}
                </button>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1 whitespace-nowrap">Quick Search</p>
                  <p className="text-xs text-blue-800 whitespace-nowrap">Enter full or partial phone number to find customers</p>
                </div>
              </div>
            </div>

            {hasSearched && (
              <div>
                {searchResults.length > 0 ? (
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3 whitespace-nowrap">
                      Found {searchResults.length} Customer{searchResults.length !== 1 ? 's' : ''}
                    </h3>
                    <div className="space-y-3">
                      {searchResults.map((customer) => (
                        <div
                          key={customer.id}
                          className="p-4 border-2 border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all cursor-pointer"
                          onClick={() => handleSelectCustomer(customer)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 text-white rounded-full font-bold text-lg">
                                {customer.name.split(' ').map(n => n[0]).join('')}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="text-base font-bold text-gray-900 whitespace-nowrap">{customer.name}</h4>
                                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-semibold rounded whitespace-nowrap">
                                    {customer.id}
                                  </span>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2">
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <i className="ri-phone-line text-gray-400"></i>
                                    <span className="whitespace-nowrap">{customer.phone}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <i className="ri-mail-line text-gray-400"></i>
                                    <span className="whitespace-nowrap">{customer.email}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <i className="ri-calendar-line text-gray-400"></i>
                                    <span className="whitespace-nowrap">Member since {customer.memberSince}</span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-4">
                                  <div className="flex items-center gap-2">
                                    <i className="ri-star-fill text-amber-500"></i>
                                    <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                                      {customer.loyaltyPoints.toLocaleString()} points
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <i className="ri-money-dollar-circle-line text-green-600"></i>
                                    <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                                      ${customer.totalSpent.toFixed(2)} total spent
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <button
                              onClick={() => handleSelectCustomer(customer)}
                              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
                            >
                              Select
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-100 text-gray-400 rounded-full mx-auto mb-4">
                      <i className="ri-phone-line text-3xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 whitespace-nowrap">No Customer Found</h3>
                    <p className="text-sm text-gray-600 mb-4 whitespace-nowrap">
                      No customer found with phone number "{phoneNumber}"
                    </p>
                    <button
                      onClick={handleCreateNewCustomer}
                      className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
                    >
                      <i className="ri-user-add-line mr-2"></i>
                      Create New Customer
                    </button>
                  </div>
                )}
              </div>
            )}

            {!hasSearched && !isSearching && (
              <div className="text-center py-12">
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 text-gray-300 rounded-full mx-auto mb-4">
                  <i className="ri-phone-line text-4xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 whitespace-nowrap">Ready to Search</h3>
                <p className="text-sm text-gray-600 whitespace-nowrap">
                  Enter a phone number above to find customers
                </p>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-end">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>

        {showCreateModal && (
          <CreateCustomerModal
            onClose={() => setShowCreateModal(false)}
            onCustomerCreated={handleCustomerCreated}
          />
        )}
      </div>
    </>
  );
}
